import { useState, useEffect } from 'react';
import { analyticsAPI, botsAPI } from '../services/api';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { format, parseISO } from 'date-fns';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 13 }}>
      <div style={{ color: 'var(--text3)', marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, display: 'flex', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>{p.value}</span>{p.name}
        </div>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [overview, setOverview]   = useState(null);
  const [chart, setChart]         = useState([]);
  const [topQ, setTopQ]           = useState([]);
  const [days, setDays]           = useState(30);
  const [botId, setBotId]         = useState('');
  const [bots, setBots]           = useState([]);

  useEffect(() => {
    botsAPI.list().then(r => setBots(r.data.bots || [])).catch(() => {});
    analyticsAPI.overview().then(r => setOverview(r.data.overview)).catch(() => {});
  }, []);

  useEffect(() => {
    const p = { days, ...(botId ? { botId } : {}) };
    analyticsAPI.chart(p).then(r => setChart(r.data.chart || [])).catch(() => {});
    analyticsAPI.topQuestions(p).then(r => setTopQ(r.data.topQuestions || [])).catch(() => {});
  }, [days, botId]);

  const chartData = chart.map(d => ({ ...d, date: format(parseISO(d.date), 'MMM d') }));

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div><h1>Analytics</h1><p>Track performance across all your chatbots</p></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select className="select" style={{ width: 160 }} value={botId} onChange={e => setBotId(e.target.value)}>
            <option value="">All bots</option>
            {bots.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
          </select>
          <div className="tabs">
            {[7, 14, 30, 90].map(d => (
              <button key={d} className={`tab${days === d ? ' active' : ''}`} onClick={() => setDays(d)}>{d}d</button>
            ))}
          </div>
        </div>
      </div>

      {/* Overview stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Total bots',         value: overview?.totalBots },
          { label: 'Total conversations', value: overview?.totalConversations },
          { label: 'Total leads',        value: overview?.totalLeads },
          { label: 'Active now',         value: overview?.activeConversations },
          { label: "Today's convs",      value: overview?.todayConversations },
          { label: "Today's leads",      value: overview?.todayLeads },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ fontSize: 28, marginTop: 8 }}>{s.value ?? '—'}</div>
          </div>
        ))}
      </div>

      {/* Activity chart */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Conversations & Leads Over Time</h2>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="ga" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--brand)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gb" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--blue)" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="var(--blue)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="conversations" name="conversations" stroke="var(--brand)" strokeWidth={2} fill="url(#ga)" />
              <Area type="monotone" dataKey="leads" name="leads" stroke="var(--blue)" strokeWidth={2} fill="url(#gb)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 14 }}>
            No data for this period
          </div>
        )}
      </div>

      {/* Top questions */}
      {topQ.length > 0 && (
        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Top Questions</h2>
          <ResponsiveContainer width="100%" height={Math.max(200, topQ.length * 42)}>
            <BarChart data={topQ} layout="vertical" margin={{ left: 0, right: 20 }}>
              <XAxis type="number" tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="question" width={260} tick={{ fill: 'var(--text2)', fontSize: 12 }} axisLine={false} tickLine={false}
                tickFormatter={v => v.length > 38 ? v.slice(0, 38) + '…' : v} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="asked" fill="var(--brand)" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
